import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Clock3,
  RotateCcw,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PageBackLink } from "../../../components/layout";
import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { Textarea } from "../../../components/ui/textarea";
import { getQuotationById, reviewQuotation } from "../../../services/quotationService";
import { useIsMobileLayout } from "../../../hooks/useMediaQuery";
import { QuotationMobileFooterBar, QuotationMobileMenuSheet, QuotationMobileHeader } from "../mobile-pages/QuotationMobileShell";
import {
  sq,
  translateQuotationType,
  translateQuotationStatus,
  seedPmcEntriesFromQuotation,
  seedTermsStateFromQuotation,
  hasQuotationPmcData,
  loadPmcEntriesForQuotation,
  loadSpecificTermsForQuotation,
  loadTermsTabState,
  formatCurrency,
  hasBackendPricingMetrics,
  resolveBackendVersionNo,
  resolveLatestBackendVersionNo,
  syncCreatedVersionLocksFromHistory,
  isLatestQuotationVersion,
  extractObjectPayload,
  isPmcCostHeadActive,
  loadStandardTermsForQuotationType,
  resolvePmcEntriesForQuotation,
  normalizeApiQuotation,
  resolveQuotationSystems,
  hydrateQuotation,
  resolveTermsFromQuotation,
  resolveBackendDerivedMultiplier,
  resolveManagerApprovalSummary,
} from "./quotationUtils";
import {
  StatusPill,
  SectionShell,
  QuotationPrimaryButton,
  QuotationSecondaryButton,
  ErrorState,
  VersionHistoryDrawer,
} from "./QuotationComponents";
import { WorkingSheetTab } from "./QuotationWorkingSheetTab";
import { CostPricingTab } from "./QuotationCostPricingTab";
import { TermsAndConditionsTab } from "./QuotationTermsTab";
import { useLazyQuotationVersionHistory } from "./quotationHooks";


function ManagerSummaryCard({ quotation, rows, pmcEntries = [], compact = false }) {
  const summary = resolveManagerApprovalSummary({ quotation, rows, pmcEntries });

  return (
    <div className={compact ? "space-y-3" : "space-y-5"}>
      <SectionShell
        title={sq("approvalSummary")}
        action={<StatusPill tone={quotation.statusTone} label={quotation.status} />}
      >
        <div
          className={
            compact
              ? "grid grid-cols-2 gap-x-3 gap-y-3.5 px-3.5 py-3.5"
              : "grid gap-y-6 px-5 py-5 sm:grid-cols-2 sm:gap-x-10 sm:px-6 lg:grid-cols-3"
          }
        >
          {[
            ["Customer", quotation.customer],
            ["Ref ID", quotation.refId],
            ["Quotation Type", quotation.type],
            ["Version", quotation.versionLabel],
            ["Total Cost", formatCurrency(summary.totalCost)],
            [
              `Gross Profit (Gross Margin ${Number(summary.grossProfitPercent || 0).toFixed(1)}%)`,
              formatCurrency(summary.grossMarginAmount),
            ],
            [
              `Net Profit (Net margin ${Number(summary.netProfitPercent || 0).toFixed(1)}%)`,
              formatCurrency(summary.netMarginAmount),
            ],
            ["Calculated Selling Price", formatCurrency(summary.calculatedSellingPrice)],
            [
              "Working Sheet Customer Selling Total",
              formatCurrency(summary.customerSellingTotal),
            ],
            [
              "Realized Gross Margin",
              `${Number(summary.realizedMarginPercent || 0).toFixed(1)}%`,
            ],
          ].map(([label, value]) => (
            <div key={label} className={compact ? "min-w-0" : undefined}>
              <p
                className={
                  compact
                    ? "mb-1 text-[10px] font-bold uppercase tracking-wider text-[#96a7c2]"
                    : "mb-1.5 text-[11px] font-bold uppercase tracking-wider text-[#96a7c2]"
                }
              >
                {label}
              </p>
              <p
                className={
                  compact
                    ? "break-words text-[13px] font-medium leading-5 text-[#2d394c]"
                    : "text-[14px] font-medium leading-6 text-[#2d394c]"
                }
              >
                {value}
              </p>
            </div>
          ))}
        </div>
      </SectionShell>

      {summary.marginBelowTarget && (
        <div className="rounded-[14px] border border-[#f0d0a7] bg-[#fff8ed] px-5 py-4 text-[#b06c16] shadow-[0_12px_28px_-24px_rgba(176,108,22,0.35)]">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="text-[13px] font-semibold">Margin warning</p>
              <p className="mt-1 text-[12px] leading-6">
                Realized Gross Margin is {Number(summary.realizedMarginPercent || 0).toFixed(1)}%, below the configured target margin. The manager can still review and decide.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ManagerDecisionBar({ quotation, onApprove, onOpenRevision, decided, compact = false }) {
  if (quotation.status !== "Pending Approval" || decided) return null;

  if (compact) {
    return (
      <div className="shrink-0 pt-3 pb-[max(0.25rem,env(safe-area-inset-bottom))]">
        <div className="flex items-stretch gap-3">
          <button
            type="button"
            onClick={onOpenRevision}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-[12px] border border-[#d0d9ea] bg-white px-3 text-[13px] font-semibold text-[#2944a8]"
          >
            <RotateCcw className="h-4 w-4 shrink-0" />
            <span className="truncate">{sq("returnForRevision")}</span>
          </button>
          <button
            type="button"
            onClick={onApprove}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-[12px] bg-[#243c93] px-3 text-[13px] font-semibold text-white"
          >
            <Check className="h-4 w-4 shrink-0" />
            <span>{sq("approve")}</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sticky bottom-0 z-20 mt-6 rounded-[14px] border border-[#d8e0ed] bg-white/95 px-5 py-4 shadow-[0_-10px_28px_-20px_rgba(30,51,131,0.32)] backdrop-blur-sm">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-[14px] font-semibold text-[#23324c]">Approval decision</p>
          <p className="mt-1 text-[12px] text-[#7c8faa]">Choose one of the two approval outcomes for this submitted quotation.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <QuotationPrimaryButton icon={Check} onClick={onApprove}>
            Approve
          </QuotationPrimaryButton>
          <QuotationSecondaryButton icon={RotateCcw} onClick={onOpenRevision}>
            Return for Revision
          </QuotationSecondaryButton>
        </div>
      </div>
    </div>
  );
}

function ReturnRevisionModal({ open, note, setNote, onConfirm, onCancel }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-130">
      <div className="absolute inset-0 bg-[#0b1430]/35" onClick={onCancel} />
      <div className="absolute left-1/2 top-1/2 w-[min(560px,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-[24px] border border-[#d8e0ed] bg-white shadow-[0_30px_60px_-30px_rgba(30,51,131,0.35)]">
        <div className="border-b border-[#d9e1ee] px-5 py-4 sm:px-6">
          <p className="text-[18px] font-semibold text-[#23324c]">Return for Revision</p>
          <p className="mt-1 text-[12px] text-[#7c8faa]">Add a revision note for the submitter before confirming this action.</p>
        </div>
        <div className="px-5 py-5 sm:px-6">
          <label className="mb-2 block text-[12px] font-semibold text-[#51657f]">Revision note</label>
          <Textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            className="min-h-[160px] rounded-[14px] border-[#cdd8ea] px-5 py-4 text-[13px] text-[#2d394c] placeholder:text-[#8e9cb2]"
            placeholder="Explain what needs to change before this quotation can be approved..."
          />
        </div>
        <div className="flex flex-wrap justify-end gap-3 border-t border-[#d9e1ee] px-5 py-4 sm:px-6">
          <QuotationSecondaryButton icon={ArrowLeft} onClick={onCancel}>
            Cancel
          </QuotationSecondaryButton>
          <QuotationPrimaryButton icon={RotateCcw} onClick={onConfirm} disabled={!note.trim()}>
            Confirm action
          </QuotationPrimaryButton>
        </div>
      </div>
    </div>
  );
}

function ManagerQuotationReview({ baseQuotation, baseHistory }) {
  const navigate = useNavigate();
  const isMobileLayout = useIsMobileLayout();
  const [activeTab, setActiveTab] = useState("workingSheet");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quotation, setQuotation] = useState(() => {
    const systems = resolveQuotationSystems(baseQuotation, baseQuotation.workingRows || []);
    return {
      ...baseQuotation,
      systems,
      systemNames: systems.map((system) => system.name),
      systemName: systems.map((system) => system.name).join(", "),
    };
  });
  const [workingRows, setWorkingRows] = useState(() => {
    const systems = resolveQuotationSystems(baseQuotation, baseQuotation.workingRows || []);
    return systems.length ? [] : (baseQuotation.workingRows || []);
  });
  const [pmcEntries, setPmcEntries] = useState([]);
  const [hasPmcConfigs, setHasPmcConfigs] = useState(false);
  const [additionalTermEntries, setAdditionalTermEntries] = useState([]);
  const [standardTerms, setStandardTerms] = useState([]);
  const [selectedStandardTerms, setSelectedStandardTerms] = useState([]);
  const [previewOrder, setPreviewOrder] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState(baseHistory);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revisionNote, setRevisionNote] = useState("");
  const [decisionDone, setDecisionDone] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const pmcConfigsLoadedRef = useRef(false);
  const termsLibraryLoadedRef = useRef(false);
  const handleVersionsLoaded = useCallback((history) => {
    syncCreatedVersionLocksFromHistory(history);
    setVersions(history);
  }, []);
  const { loading: historyLoading, error: historyError } = useLazyQuotationVersionHistory({
    open: historyOpen,
    refId: quotation.refId || baseQuotation.refId,
    fallbackHistory: versions,
    onLoaded: handleVersionsLoaded,
    prefetch: true,
  });

  useEffect(() => {
    // Discover systems from header fields + mapped workingRows; keep display rows empty for lazy load.
    const systems = resolveQuotationSystems(baseQuotation, baseQuotation.workingRows || []);
    setQuotation({
      ...baseQuotation,
      systems,
      systemNames: systems.map((system) => system.name),
      systemName: systems.map((system) => system.name).join(", "),
    });
    setVersions(baseHistory);
    setDecisionDone(false);
    setActionError("");
    setWorkingRows(systems.length ? [] : (baseQuotation.workingRows || []));
  }, [baseQuotation, baseHistory]);

  const latestVersionNo = resolveLatestBackendVersionNo(versions);
  const isLatestVersion = isLatestQuotationVersion(quotation, versions);
  const summaryRows = workingRows.length ? workingRows : (quotation.workingRows || []);
  const canDecide =
    quotation.status === "Pending Approval" &&
    !decisionDone &&
    !actionBusy &&
    isLatestVersion;

  function goMobileTab(direction) {
    const order = ["workingSheet", "costPricing", "terms", "summary"];
    const index = order.indexOf(activeTab);
    const nextIndex =
      direction === "next"
        ? Math.min(order.length - 1, index + 1)
        : Math.max(0, index - 1);
    setActiveTab(order[nextIndex]);
  }

  useEffect(() => {
    pmcConfigsLoadedRef.current = false;
    termsLibraryLoadedRef.current = false;
    setPmcEntries([]);
    setHasPmcConfigs(false);
    setStandardTerms([]);
    setSelectedStandardTerms([]);
    setAdditionalTermEntries([]);
    setPreviewOrder([]);
  }, [baseQuotation.id, baseQuotation.type]);

  // Load PMC once for approval Summary totals (and C+P), not only when Pricing is opened.
  useEffect(() => {
    let active = true;

    async function loadPmcForApproval() {
      const quotationPmcs = baseQuotation.pmcs || [];
      if (hasQuotationPmcData(quotationPmcs)) {
        const seeded = seedPmcEntriesFromQuotation(quotationPmcs);
        setHasPmcConfigs(seeded.length > 0);
        setPmcEntries(seeded);
      }
      try {
        const loaded = await loadPmcEntriesForQuotation(baseQuotation.id, quotationPmcs);
        if (!active) return;

        if (loaded.source === "quotation" || loaded.source === "embedded") {
          try {
            const { entries, hasPmcConfigs: hasConfigs } = await resolvePmcEntriesForQuotation(
              baseQuotation.type,
              loaded.entries
            );
            if (!active) return;
            pmcConfigsLoadedRef.current = true;
            const nextEntries = entries.length
              ? entries
              : loaded.entries.filter((entry) => isPmcCostHeadActive(entry));
            setHasPmcConfigs(hasConfigs || nextEntries.length > 0);
            setPmcEntries(nextEntries);
            setQuotation((current) => ({ ...current, pmcs: nextEntries }));
            return;
          } catch {
            if (!active) return;
            const nextEntries = loaded.entries.filter((entry) => isPmcCostHeadActive(entry));
            pmcConfigsLoadedRef.current = true;
            setHasPmcConfigs(nextEntries.length > 0);
            setPmcEntries(nextEntries);
            setQuotation((current) => ({ ...current, pmcs: nextEntries }));
            return;
          }
        }

        if (pmcConfigsLoadedRef.current) return;
        const { entries, hasPmcConfigs: hasConfigs } = await resolvePmcEntriesForQuotation(
          baseQuotation.type,
          []
        );
        if (!active) return;
        pmcConfigsLoadedRef.current = true;
        setHasPmcConfigs(hasConfigs);
        setPmcEntries(entries);
      } catch {
        if (!active) return;
        if (!pmcConfigsLoadedRef.current) {
          setHasPmcConfigs(hasQuotationPmcData(quotationPmcs));
          setPmcEntries(seedPmcEntriesFromQuotation(quotationPmcs));
          pmcConfigsLoadedRef.current = true;
        }
      }
    }

    loadPmcForApproval();
    return () => {
      active = false;
    };
  }, [baseQuotation.id, baseQuotation.type, baseQuotation.pmcs]);

  useEffect(() => {
    if (activeTab !== "terms") return undefined;

    let active = true;

    async function loadTermsForTab() {
      if (termsLibraryLoadedRef.current) return;
      const quotationTerms = baseQuotation.specificTerms || [];
      try {
        const state = await loadTermsTabState(
          baseQuotation.id,
          baseQuotation.type,
          quotationTerms
        );
        if (!active) return;
        termsLibraryLoadedRef.current = true;
        setStandardTerms(state.standardTerms);
        setSelectedStandardTerms(state.selectedIds);
        setAdditionalTermEntries(state.additionalTermEntries);
        setPreviewOrder(state.previewOrder);
        if (state.specificTerms.length) {
          setQuotation((current) => ({ ...current, specificTerms: state.specificTerms }));
        }
      } catch {
        if (!active) return;
        // Still pull /specific-terms so a library error cannot blank saved selections.
        try {
          const [nextTerms, loaded] = await Promise.all([
            loadStandardTermsForQuotationType(baseQuotation.type).catch(() => []),
            loadSpecificTermsForQuotation(baseQuotation.id, quotationTerms),
          ]);
          if (!active) return;
          const specificTerms =
            loaded.source === "quotation" || loaded.source === "embedded"
              ? loaded.specificTerms
              : quotationTerms;
          const resolved = resolveTermsFromQuotation(specificTerms, nextTerms || []);
          setStandardTerms(nextTerms || []);
          setSelectedStandardTerms(resolved.selectedIds);
          setAdditionalTermEntries(resolved.additionalTermEntries);
          setPreviewOrder(resolved.previewOrder);
          if (specificTerms.length) {
            setQuotation((current) => ({ ...current, specificTerms }));
          }
        } catch {
          if (!active) return;
          const resolved = seedTermsStateFromQuotation(quotationTerms);
          setStandardTerms([]);
          setSelectedStandardTerms(resolved.selectedIds);
          setAdditionalTermEntries(resolved.additionalTermEntries);
          setPreviewOrder(resolved.previewOrder);
        }
        termsLibraryLoadedRef.current = true;
      }
    }

    loadTermsForTab();
    return () => {
      active = false;
    };
  }, [activeTab, baseQuotation.id, baseQuotation.type]);

  async function approveQuotation() {
    setActionBusy(true);
    setActionError("");

    try {
      if (!quotation.id) throw new Error("Quotation ID is missing.");

      const reviewResponse = await reviewQuotation(quotation.id, {
        action: "APPROVE",
        managerNote: revisionNote.trim(),
      });
      const reviewedPayload =
        extractObjectPayload(reviewResponse?.data) ||
        extractObjectPayload(reviewResponse) ||
        null;

      let next = reviewedPayload ? normalizeApiQuotation(reviewedPayload) : null;
      if (!next?.refId) {
        const latest = await getQuotationById(quotation.id);
        next = normalizeApiQuotation(latest?.data || {});
      }

      if (!next.refId) {
        throw new Error("Backend did not return the updated quotation after approval.");
      }

      setQuotation(next);
      setDecisionDone(true);
    } catch (error) {
      setActionError(error.message || "Failed to approve quotation.");
    } finally {
      setActionBusy(false);
    }
  }

  async function confirmRevision() {
    setActionBusy(true);
    setActionError("");

    try {
      if (!quotation.id) throw new Error("Quotation ID is missing.");
      const note = revisionNote.trim();
      if (!note) throw new Error("A revision note is required.");

      const reviewResponse = await reviewQuotation(quotation.id, {
        action: "REJECT",
        managerNote: note,
      });
      const reviewedPayload =
        extractObjectPayload(reviewResponse?.data) ||
        extractObjectPayload(reviewResponse) ||
        null;

      let next = reviewedPayload
        ? normalizeApiQuotation({ ...reviewedPayload, managerNote: reviewedPayload.managerNote || note })
        : null;

      if (!next?.refId) {
        const latest = await getQuotationById(quotation.id);
        next = normalizeApiQuotation({
          ...(latest?.data || {}),
          managerNote: note,
        });
      }

      if (!next.refId) {
        throw new Error("Backend did not return the updated quotation after revision.");
      }

      // Keep the same quotation version and use backend status as source of truth.
      if (!next.revisionNote) {
        next = hydrateQuotation({
          ...next,
          revisionNote: note,
          submissionNote: "",
        });
      }

      setQuotation(next);
      setDecisionDone(true);
      setShowRevisionModal(false);
      setRevisionNote("");
    } catch (error) {
      setActionError(error.message || "Failed to return quotation for revision.");
    } finally {
      setActionBusy(false);
    }
  }

  const tabPanels = (
    <>
      <div className={activeTab === "workingSheet" ? "block" : "hidden"} aria-hidden={activeTab !== "workingSheet"}>
        <WorkingSheetTab
          quotationId={quotation.id}
          quotationType={quotation.type}
          rows={workingRows}
          setRows={setWorkingRows}
          editable={false}
          systems={resolveQuotationSystems(quotation, quotation.workingRows || [])}
          costPricingMultiplier={resolveBackendDerivedMultiplier(
            quotation.pricingMetrics,
            quotation.type
          )}
          backendSheetTotals={quotation.pricingMetrics ? {
            supplyInputAmount: quotation.pricingMetrics.totalInputSupplyAmount,
            inputInstallationAmount: quotation.pricingMetrics.totalInputInstallationAmount,
            supplySubtotal: quotation.pricingMetrics.totalSellingSupplyAmount,
            installationSubtotal: quotation.pricingMetrics.totalSellingInstallationAmount,
          } : null}
          preferBackendTotals={hasBackendPricingMetrics(quotation.pricingMetrics)}
        />
      </div>
      {activeTab === "costPricing" ? (
        <CostPricingTab
          quotationType={quotation.type}
          rows={workingRows}
          editable={false}
          pmcEntries={pmcEntries}
          hasPmcConfigs={hasPmcConfigs}
          overheadsPercent={quotation.overheadsPct}
          markUpPercent={quotation.markUpPct}
          netPricePercentage={quotation.netPricePercentage}
          backendPricing={quotation.pricingMetrics}
          preferBackendPricing={hasBackendPricingMetrics(quotation.pricingMetrics)}
          forceBackendSheetCosts
        />
      ) : null}
      {activeTab === "terms" ? (
        <TermsAndConditionsTab
          standardTerms={standardTerms}
          selectedStandardTerms={selectedStandardTerms}
          setSelectedStandardTerms={setSelectedStandardTerms}
          additionalTermEntries={additionalTermEntries}
          setAdditionalTermEntries={setAdditionalTermEntries}
          previewOrder={previewOrder}
          setPreviewOrder={setPreviewOrder}
          editable={false}
          quotationType={quotation.type}
        />
      ) : null}
      {isMobileLayout && activeTab === "summary" ? (
        <ManagerSummaryCard
          quotation={quotation}
          rows={summaryRows}
          pmcEntries={pmcEntries}
          compact
        />
      ) : null}
    </>
  );

  return (
    <div className={isMobileLayout ? "flex min-h-0 flex-1 flex-col" : "space-y-5 pb-6"}>
      <div
        className={
          isMobileLayout
            ? "min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain"
            : "contents"
        }
      >
        {isMobileLayout ? (
          <QuotationMobileHeader
            refId={quotation.refId}
            onBack={() => navigate("/sales/quotations/quotation")}
            onOpenMenu={() => setMobileMenuOpen(true)}
            status={translateQuotationStatus(quotation.status)}
            versionLabel={quotation.versionLabel}
            typeLabel={translateQuotationType(quotation.type)}
          />
        ) : (
          <>
            <PageTitleRow className="mb-5" back={<PageBackLink to="/sales/quotations/quotation" />}>
              <h1>
                <Trans i18nKey="salesQuotation.quotationDetails">Quotation details</Trans>
              </h1>
            </PageTitleRow>
            <div className="rounded-[14px] border border-[#d8e0ed] bg-white px-5 py-6 shadow-[0_14px_30px_-26px_rgba(30,51,131,0.28)] sm:px-7">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <h2 className="text-[24px] font-semibold tracking-[0.01em] text-[#23324c]">{quotation.refId}</h2>
                    <StatusPill tone={quotation.statusTone} label={quotation.status} />
                    <span className="rounded-full bg-[#eef2f8] px-3 py-1.5 text-[12px] font-semibold text-[#647b9f]">{quotation.versionLabel}</span>
                    {quotation.expired && <StatusPill tone="rejected" label="Expired" />}
                  </div>
                  <p className="text-[14px] leading-7 text-[#7384a2]">{quotation.customer} · {quotation.type} · {quotation.location}</p>
                  <p className="text-[12px] text-[#8ea0bd]">This approval view is read-only while the quotation is pending review.</p>
                </div>
                <QuotationSecondaryButton icon={Clock3} onClick={() => setHistoryOpen(true)}>
                  View Previous Versions
                </QuotationSecondaryButton>
              </div>
            </div>
          </>
        )}

        {!isLatestVersion && (
          <div className="rounded-[14px] border border-[#d8e0ed] bg-[#f7faff] px-5 py-3 text-[#5d7598]">
            <p className="text-[12px] font-semibold">Viewing a previous version (read-only).</p>
            <p className="mt-0.5 text-[12px]">
              Only the current version ({latestVersionNo != null ? `V${latestVersionNo}` : "latest"}) can be edited or approved.
            </p>
          </div>
        )}

        {actionError && <ErrorState message={actionError} onRetry={() => setActionError("")} />}

        {decisionDone && (
          <div className="rounded-[14px] border border-[#d5e8dc] bg-[#f4fff8] px-5 py-4 text-[#2b7a4b]">
            <p className="text-[13px] font-semibold">
              {quotation.status === "Approved"
                ? "Quotation approved successfully."
                : "Quotation returned for revision successfully."}
            </p>
            <p className="mt-1 text-[12px]">
              Status is now <span className="font-semibold">{quotation.status}</span>
              {quotation.status === "Draft" && quotation.revisionNote
                ? " and the revision note has been attached. The same version is editable again."
                : quotation.status === "Approved"
                  ? ". Quotation remains locked and PDF download is available."
                  : "."}
            </p>
          </div>
        )}

        {quotation.revisionNote && quotation.status === "Draft" && (
          <div className="rounded-[14px] border border-[#f0d0a7] bg-[#fff8ed] px-5 py-4 text-[#b06c16]">
            <p className="text-[13px] font-semibold">Revision note</p>
            <p className="mt-1 text-[12px] leading-6">{quotation.revisionNote}</p>
          </div>
        )}

        {/* Desktop keeps approval summary above tabs; mobile shows it only in Summary tab. */}
        {!isMobileLayout ? (
          <ManagerSummaryCard quotation={quotation} rows={summaryRows} pmcEntries={pmcEntries} />
        ) : null}

        <div className={`space-y-3 ${isMobileLayout ? "space-y-2.5" : ""}`}>
          {isMobileLayout ? (
            <div className="-mx-1 flex border-b border-[#e1e7f2] bg-white">
              {[
                { key: "workingSheet", label: sq("mobileTabItems") },
                { key: "costPricing", label: sq("mobileTabPricing") },
                { key: "terms", label: sq("mobileTabTerms") },
                { key: "summary", label: sq("mobileTabSummary") },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`min-h-[42px] flex-1 px-1 text-[12px] font-bold transition ${
                    activeTab === tab.key
                      ? "border-b-[3px] border-[#243c93] text-[#243c93]"
                      : "border-b-[3px] border-transparent text-[#8ea0bd]"
                  }`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap items-end gap-6 border-b border-[#d7dfec] px-1">
              {[
                { key: "workingSheet", label: "Working Sheet" },
                { key: "costPricing", label: "Cost + Pricing", badge: "INTERNAL" },
                { key: "terms", label: "Terms & Conditions" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`relative pb-5 text-[15px] font-semibold transition ${activeTab === tab.key ? "text-[#2944a8]" : "text-[#6f82a1]"}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  <span>{tab.label}</span>
                  {tab.badge && <span className="ml-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#ef8c16]">{tab.badge}</span>}
                  {activeTab === tab.key && <span className="absolute inset-x-0 -bottom-px h-[3px] rounded-full bg-[#2944a8]" />}
                </button>
              ))}
            </div>
          )}

          {tabPanels}
        </div>
      </div>

      {isMobileLayout ? (
        activeTab === "summary" && canDecide ? (
          <ManagerDecisionBar
            quotation={quotation}
            onApprove={approveQuotation}
            onOpenRevision={() => setShowRevisionModal(true)}
            decided={false}
            compact
          />
        ) : (
          <div className="shrink-0 pt-3">
            <QuotationMobileFooterBar
              activeTab={activeTab}
              showSubmit={false}
              actionBusy={actionBusy}
              submittingLabel=""
              submitLabel={sq("approve")}
              waitingLabel={
                decisionDone
                  ? quotation.status === "Approved"
                    ? sq("statusApproved")
                    : sq("returnForRevision")
                  : activeTab === "summary"
                    ? sq("mobileWaitingApproval")
                    : sq("mobileNextSummary")
              }
              onBackTab={() => goMobileTab("back")}
              onNextTab={() => goMobileTab("next")}
              onSubmit={() => {}}
            />
          </div>
        )
      ) : (
        <ManagerDecisionBar
          quotation={quotation}
          onApprove={approveQuotation}
          onOpenRevision={() => setShowRevisionModal(true)}
          decided={decisionDone || actionBusy || !isLatestVersion}
        />
      )}

      {isMobileLayout ? (
        <QuotationMobileMenuSheet
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          showCreateNewVersion={false}
          createVersionDisabled
          onCreateNewVersion={() => {}}
          onVersionHistory={() => setHistoryOpen(true)}
          showDownload={false}
          onDownloadPdf={() => {}}
          isServiceQuotation={false}
          complaintId=""
          complaintBackBusy={false}
          onBackToComplaint={() => {}}
          actionBusy={actionBusy}
        />
      ) : null}

      <VersionHistoryDrawer
        open={historyOpen}
        versions={versions}
        latestVersionNo={latestVersionNo}
        viewingVersionNo={resolveBackendVersionNo(quotation)}
        viewingId={quotation.id}
        loading={historyLoading}
        error={historyError}
        onClose={() => setHistoryOpen(false)}
        onSelectVersion={(version) => {
          setHistoryOpen(false);
          navigate(`/sales/quotations/quotation/${version.id ?? version.refId}`, {
            state: {
              quotation: version,
              versionHistory: versions,
            },
          });
        }}
      />
      <ReturnRevisionModal
        open={showRevisionModal}
        note={revisionNote}
        setNote={setRevisionNote}
        onConfirm={confirmRevision}
        onCancel={() => {
          setShowRevisionModal(false);
          setRevisionNote("");
        }}
      />
    </div>
  );
}

export {
  ManagerQuotationReview
};
